// Reveals [data-reveal] elements as they enter the viewport. Falls back to
// immediately-visible if IntersectionObserver isn't available, and force-
// reveals anything left over after REVEAL_TIMEOUT_MS — some environments
// (backgrounded/prerendered tabs, certain webviews) never fire IO callbacks,
// and stale content is worse than a missed animation.
const REVEAL_TIMEOUT_MS = 2000
const COUNTUP_DURATION_MS = 1200

// Animates [data-countup] numbers inside a just-revealed element from 0 to
// their target value. Skips straight to the final value for reduced motion.
function startCountups(root) {
  root.querySelectorAll("[data-countup]").forEach((el) => {
    if (el.dataset.countupStarted) return
    el.dataset.countupStarted = "true"

    const target = parseInt(el.dataset.countup.replace(/[^\d]/g, ""), 10)
    if (Number.isNaN(target)) return

    const format = (n) => n.toLocaleString("en-US")
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.textContent = format(target)
      return
    }

    const startedAt = performance.now()
    let done = false
    const frame = (now) => {
      if (done) return
      const progress = Math.min((now - startedAt) / COUNTUP_DURATION_MS, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      el.textContent = format(Math.round(target * eased))
      if (progress < 1) requestAnimationFrame(frame)
      else done = true
    }
    requestAnimationFrame(frame)
    // rAF is throttled in backgrounded tabs — make sure the final value lands
    setTimeout(() => {
      if (!done) {
        done = true
        el.textContent = format(target)
      }
    }, COUNTUP_DURATION_MS + 200)
  })
}

export function initScrollReveal() {
  const targets = document.querySelectorAll("[data-reveal]")
  if (!targets.length) return

  const revealAll = () =>
    targets.forEach((el) => {
      el.classList.add("is-revealed")
      startCountups(el)
    })

  if (!("IntersectionObserver" in window)) {
    revealAll()
    return
  }

  // A working IntersectionObserver always delivers an initial callback batch
  // right after observe() (even with nothing intersecting). If that never
  // happens, IO is broken in this environment — fall back to revealing all.
  let observerFired = false

  const observer = new IntersectionObserver(
    (entries) => {
      observerFired = true
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-revealed")
          startCountups(entry.target)
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
  )

  targets.forEach((el) => observer.observe(el))

  setTimeout(() => {
    if (observerFired) return
    revealAll()
    observer.disconnect()
  }, REVEAL_TIMEOUT_MS)
}
